import { useCallback } from 'react';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';

import { useLastInteractedStore } from '../../context/LastInteractedStore';
import { useIsCustomerAddress } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A "placeholder" route that will always redirect users to the menu or
 * delivery menu route if the location slug or address ID is available,
 * or to the location selection route if it is not.
 */
export const MenuWithoutSlug = () => {
  const router = useRouter();

  const lastInteractedStore = useLastInteractedStore();
  const { restaurantSlug, addressId } = lastInteractedStore ?? {};

  const isCustomerAddress = useIsCustomerAddress(addressId);

  const routeParams = useLocalSearchParams<{ category?: string }>();
  const category = routeParams.category;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const redirectToMenuOrLocation = useCallback(() => {
    const hasValidMenuRouteSlug = restaurantSlug !== undefined;
    const hasValidDeliveryMenuRouteSlug = addressId && isCustomerAddress;

    // if the last interacted store is a delivery location, navigate to the
    // associated delivery menu screen
    if (hasValidDeliveryMenuRouteSlug) {
      router.replace({ pathname: '/delivery/[addressId]/menu', params: { addressId, ...(category ? { category } : {}) } }); // prettier-ignore
      return;
    }

    // if the last interacted store is a pickup or outpost location, navigate
    // to the associated menu screen
    if (hasValidMenuRouteSlug) {
      router.replace({ pathname: '/[restaurantSlug]/menu', params: { restaurantSlug, ...(category ? { category } : {}) } }); // prettier-ignore
      return;
    }

    // otherwise navigate to the location search screen
    router.replace({ pathname: '/locations', params: category ? { category } : {} });
  }, [addressId, category, isCustomerAddress, restaurantSlug, router]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useFocusEffect(redirectToMenuOrLocation);

  // ─────────────────────────────────────────────────────────────────────

  return null;
};
